import { useMemo, useState } from 'react';

import { AppIcon, FullScreenLoading, TIER_TO_TRACK_CODE } from '@backrs_ui/common-ui';
import { type CareerPath } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CareerPathDetailPage } from '@backrs_ui/grow-ui/src/pages/CareerPath/CareerPathDetailPage';
import { CareerPathPage } from '@backrs_ui/grow-ui/src/pages/CareerPath/CareerPathPage';
import {
  getCareerPathAIDisruptionByPathId,
  getCareerPathCompensationsByPathId,
  getCareerPathSampleJobsByPathId,
} from '@backrs_ui/grow-ui/src/pages/CareerPath/util';
import { hydrateEducationalInvestment } from '@backrs_ui/grow-ui/src/pages/EducationalInvestment/utils';
import { BlueprintOutputPage } from '@backrs_ui/grow-ui/src/pages/Result/BlueprintOutputPage';
import { useGetMemberBlueprintQuery } from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import { useGetEducationalInvestmentsByCareerTrackQuery } from '@backrs_ui/grow-ui/src/redux/educationalInvestment/educationalInvestment.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { getSurveyResults } from '@backrs_ui/grow-ui/src/redux/userResponse/thunks/userResponse.thunk';

import { Box, Button, type SxProps, type Theme } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { useAgentContext } from './MemberBlueprintView.agentContext';
import { MemberTabErrorState, TabErrorMessage } from './MemberTabErrorState';
import { useCurrentMember } from './useCurrentMember';

const {
  modules: { coachingDashboard },
} = appStrings;

export const MemberBlueprintView = () => {
  // Swapping content in place rather than routing — routing away closes the tab.
  const [isViewingCareerPaths, setIsViewingCareerPaths] = useState(false);
  const [openCareerPath, setOpenCareerPath] = useState<CareerPath | null>(null);

  const currentMember = useCurrentMember();
  const { member, isLoading: isLoadingMember, isError: isMemberError } = currentMember;

  const {
    data,
    isLoading: isLoadingBlueprint,
    isError,
  } = useGetMemberBlueprintQuery(member ? member.id : skipToken);

  const careerQuestions = useAppSelector((state) => state.survey.careerQuestions);
  const {
    careerCategories,
    careerPaths,
    careerPathRoles,
    careerPathMeanings,
    careerPathEnjoyments,
    careerPathCompensations,
    careerPathSampleJobs,
    careerPathAIDisruptions,
    careerTracks,
  } = useAppSelector((state) => state.careerPath);
  const referenceDataFailed = useAppSelector(
    (state) => !!state.survey.error || !!state.careerPath.error,
  );

  // Scoring against a question bank that has not finished loading yields a wrong tier, not no tier.
  const hasReferenceData = careerQuestions.length > 0 && careerPaths.length > 0;

  const userResponseSet = data?.userResponseSet;

  const surveyResults = useMemo(
    () =>
      userResponseSet && hasReferenceData
        ? getSurveyResults({
            userResponseSet,
            careerQuestions,
            careerPaths,
            careerPathRoles,
            careerPathMeanings,
            careerPathEnjoyments,
          })
        : undefined,
    [
      userResponseSet,
      hasReferenceData,
      careerQuestions,
      careerPaths,
      careerPathRoles,
      careerPathMeanings,
      careerPathEnjoyments,
    ],
  );

  const { data: rawEducationalInvestment } = useGetEducationalInvestmentsByCareerTrackQuery({
    careerTrackCode: surveyResults ? TIER_TO_TRACK_CODE[surveyResults.tier] : undefined,
  });

  const educationalInvestment = useMemo(
    () =>
      rawEducationalInvestment
        ? {
            target: rawEducationalInvestment.target.map(hydrateEducationalInvestment),
            others: rawEducationalInvestment.others.map(hydrateEducationalInvestment),
          }
        : undefined,
    [rawEducationalInvestment],
  );

  useAgentContext(member);

  if (isMemberError) return <MemberTabErrorState {...currentMember} />;

  if (isLoadingMember || isLoadingBlueprint || (!hasReferenceData && !referenceDataFailed)) {
    return <FullScreenLoading fillContainer />;
  }

  if (!member) return null;

  if (isError || !userResponseSet || !surveyResults) {
    return <TabErrorMessage message={coachingDashboard.blueprintError} />;
  }

  const backButton = (label: string, onClick: () => void) => (
    <Button
      id="member-blueprint-career-paths-back-button"
      hierarchy="Tertiary color"
      size="md"
      onClick={onClick}
      startIcon={<AppIcon iconName="ArrowLeft" diameter="20px" color="currentColor" />}
      sx={backButtonSx}
    >
      {label}
    </Button>
  );

  if (openCareerPath) {
    return (
      <Box sx={containerSx}>
        {backButton(coachingDashboard.backToCareerPaths, () => setOpenCareerPath(null))}

        <CareerPathDetailPage
          careerPath={openCareerPath}
          careerPathCompensations={getCareerPathCompensationsByPathId(
            openCareerPath.id,
            careerPathCompensations,
          )}
          careerPathSampleJobs={getCareerPathSampleJobsByPathId(
            openCareerPath.id,
            careerPathSampleJobs,
            careerTracks,
          )}
          careerPaths={careerPaths}
          careerPathSuggestions={surveyResults.careerPathSuggestions}
          careerPathAIDisruption={getCareerPathAIDisruptionByPathId(
            careerPathAIDisruptions,
            openCareerPath.id,
          )}
          responseSetId={userResponseSet.id}
          isReadOnly
        />
      </Box>
    );
  }

  if (isViewingCareerPaths) {
    return (
      <Box sx={containerSx}>
        {backButton(coachingDashboard.backToBlueprint, () => setIsViewingCareerPaths(false))}

        <CareerPathPage
          careerCategories={careerCategories}
          careerPaths={careerPaths}
          careerPathSuggestions={surveyResults.careerPathSuggestions}
          userHasBlueprint
          showHeaderActions={false}
          onCareerPathSelect={setOpenCareerPath}
        />
      </Box>
    );
  }

  return (
    <Box sx={containerSx}>
      <BlueprintOutputPage
        tier={surveyResults.tier}
        careerPathSuggestions={surveyResults.careerPathSuggestions}
        userResponseSet={userResponseSet}
        educationalInvestment={educationalInvestment}
        owner={{
          name: `${member.first_name} ${member.last_name}`,
          profileImageUrl: member.profile_image,
          aspirationsCategories: data.aspirations_categories,
        }}
        onViewAllCareerPaths={() => setIsViewingCareerPaths(true)}
        onCareerPathSelect={setOpenCareerPath}
        isReadOnly
      />
    </Box>
  );
};

const backButtonSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  // a flex item defaults to min-width:auto and refuses to shrink below its content, clipping the page
  minWidth: 0,
  paddingRight: backrs.spacing['spacing-md'],
});
